import React, { useEffect, useMemo, useState } from "react";
import { Wrench, Plus, X, RefreshCcw, FileText } from "lucide-react";
import TopBar from "../../components/TopBar";

function fmtDateIT(iso) {
    if (!iso) return "";
    // iso "YYYY-MM-DD"
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return iso;
    return `${m[3]}/${m[2]}/${m[1]}`;
}

export default function Manutenzioni() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [items, setItems] = useState([]);

    // paginazione semplice
    const [page, setPage] = useState(1);
    const limit = 50;
    const [total, setTotal] = useState(0);

    // modal
    const [open, setOpen] = useState(false);

    // form
    const todayIso = useMemo(() => {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    }, []);

    const [form, setForm] = useState({
        data_intervento: todayIso,
        sede_intervento: "",
        tipo_intervento: "",
        firma: "",
    });

    async function load() {
        setLoading(true);
        setError("");
        try {
            const r = await fetch(
                `/api/manutenzioni/list.php?page=${page}&limit=${limit}`,
                { credentials: "include" }
            );
            const j = await r.json().catch(() => null);

            if (!r.ok || !j?.success) {
                throw new Error(j?.error || "Errore caricamento storico");
            }
            setItems(Array.isArray(j.items) ? j.items : []);
            setTotal(Number(j.total || 0));
        } catch (e) {
            setError(e?.message || "Errore caricamento");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    function resetForm() {
        setForm({
            data_intervento: todayIso,
            sede_intervento: "",
            tipo_intervento: "",
            firma: "",
        });
    }

    function openModal() {
        resetForm();
        setOpen(true);
    }

    async function save() {
        setSaving(true);
        setError("");
        try {
            if (!form.data_intervento) throw new Error("Data obbligatoria");
            if (!form.sede_intervento.trim()) throw new Error("Sede intervento obbligatoria");
            if (!form.tipo_intervento.trim()) throw new Error("Tipo intervento obbligatorio");

            const r = await fetch("/api/manutenzioni/save.php", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    data_intervento: form.data_intervento,
                    sede_intervento: form.sede_intervento.trim(),
                    tipo_intervento: form.tipo_intervento.trim(),
                    firma: form.firma.trim(),
                }),
            });

            const j = await r.json().catch(() => null);
            if (!r.ok || !j?.success) {
                throw new Error(j?.error || "Errore salvataggio");
            }

            setOpen(false);

            // ricarico prima pagina per vedere subito l’ultimo inserito
            if (page !== 1) setPage(1);
            else load();
        } catch (e) {
            setError(e?.message || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    }

    const pages = useMemo(() => {
        const p = Math.ceil((total || 0) / limit);
        return p < 1 ? 1 : p;
    }, [total]);

    return (
        <>
            <TopBar />

            <div className="p-4 md:p-6">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <Wrench className="w-5 h-5" />
                            <h1 className="text-xl md:text-2xl font-semibold">Manutenzioni straordinarie</h1>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                            Inserisci interventi e stampa il PDF singolo con carta intestata e filigrana.
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={load}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-gray-50"
                            title="Aggiorna"
                        >
                            <RefreshCcw className="w-4 h-4" />
                            <span className="hidden sm:inline">Aggiorna</span>
                        </button>

                        <button
                            onClick={openModal}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-black text-white hover:opacity-90"
                        >
                            <Plus className="w-4 h-4" />
                            Nuova
                        </button>
                    </div>
                </div>

                {error ? (
                    <div className="mt-4 p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm">
                        {error}
                    </div>
                ) : null}

                <div className="mt-4 rounded-xl border bg-white overflow-hidden">
                    <div className="p-3 border-b flex items-center justify-between">
                        <div className="text-sm font-medium">Storico</div>
                        <div className="text-xs text-gray-500">
                            Totale: <span className="font-semibold">{total}</span>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-gray-700">
                                <tr>
                                    <th className="text-left px-3 py-2 w-[130px]">Data</th>
                                    <th className="text-left px-3 py-2 w-[240px]">Sede</th>
                                    <th className="text-left px-3 py-2">Tipo intervento</th>
                                    <th className="text-left px-3 py-2 w-[180px]">Firma</th>
                                    <th className="text-right px-3 py-2 w-[120px]">PDF</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td className="px-3 py-3 text-gray-500" colSpan={5}>
                                            Caricamento...
                                        </td>
                                    </tr>
                                ) : items.length === 0 ? (
                                    <tr>
                                        <td className="px-3 py-3 text-gray-500" colSpan={5}>
                                            Nessuna manutenzione inserita.
                                        </td>
                                    </tr>
                                ) : (
                                    items.map((it) => (
                                        <tr key={it.id} className="border-t">
                                            <td className="px-3 py-2 whitespace-nowrap">{fmtDateIT(it.data_intervento)}</td>
                                            <td className="px-3 py-2">{it.sede_intervento}</td>
                                            <td className="px-3 py-2">
                                                <div className="line-clamp-2 whitespace-pre-wrap">{it.tipo_intervento}</div>
                                            </td>
                                            <td className="px-3 py-2">{it.firma || ""}</td>
                                            <td className="px-3 py-2 text-right">
                                                <a
                                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border hover:bg-gray-50"
                                                    href={`/api/manutenzioni/pdf-one.php?id=${it.id}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    title="Apri PDF"
                                                >
                                                    <FileText className="w-4 h-4" />
                                                    PDF
                                                </a>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="p-3 border-t flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                            Pagina <span className="font-semibold">{page}</span> /{" "}
                            <span className="font-semibold">{pages}</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                className="px-3 py-1.5 rounded-lg border disabled:opacity-40"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                                Indietro
                            </button>
                            <button
                                className="px-3 py-1.5 rounded-lg border disabled:opacity-40"
                                disabled={page >= pages}
                                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                            >
                                Avanti
                            </button>
                        </div>
                    </div>
                </div>

                {open ? (
                    <div className="fixed inset-0 z-50">
                        <div
                            className="absolute inset-0 bg-black/40"
                            onClick={() => !saving && setOpen(false)}
                        />
                        <div className="absolute inset-0 flex items-center justify-center p-4">
                            <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl border overflow-hidden">
                                <div className="p-4 border-b flex items-center justify-between">
                                    <div className="font-semibold">Nuova manutenzione</div>
                                    <button
                                        onClick={() => !saving && setOpen(false)}
                                        className="p-2 rounded-lg hover:bg-gray-100"
                                        title="Chiudi"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs text-gray-600">Data</label>
                                        <input
                                            type="date"
                                            className="mt-1 w-full border rounded-lg px-3 py-2"
                                            value={form.data_intervento}
                                            onChange={(e) =>
                                                setForm((f) => ({ ...f, data_intervento: e.target.value }))
                                            }
                                        />
                                    </div>

                                    <div>
                                        <label className="text-xs text-gray-600">Sede intervento</label>
                                        <input
                                            type="text"
                                            className="mt-1 w-full border rounded-lg px-3 py-2"
                                            placeholder="Es. Cucina, Magazzino, Punto vendita..."
                                            value={form.sede_intervento}
                                            onChange={(e) =>
                                                setForm((f) => ({ ...f, sede_intervento: e.target.value }))
                                            }
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="text-xs text-gray-600">Tipo intervento</label>
                                        <textarea
                                            className="mt-1 w-full border rounded-lg px-3 py-2 min-h-[120px]"
                                            placeholder="Descrivi l'intervento effettuato..."
                                            value={form.tipo_intervento}
                                            onChange={(e) =>
                                                setForm((f) => ({ ...f, tipo_intervento: e.target.value }))
                                            }
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="text-xs text-gray-600">Firma (facoltativa)</label>
                                        <input
                                            type="text"
                                            className="mt-1 w-full border rounded-lg px-3 py-2"
                                            placeholder="Nome e Cognome"
                                            value={form.firma}
                                            onChange={(e) => setForm((f) => ({ ...f, firma: e.target.value }))}
                                        />
                                    </div>
                                </div>

                                <div className="p-4 border-t flex items-center justify-end gap-2">
                                    <button
                                        className="px-4 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                                        disabled={saving}
                                        onClick={() => setOpen(false)}
                                    >
                                        Annulla
                                    </button>
                                    <button
                                        className="px-4 py-2 rounded-lg bg-black text-white hover:opacity-90 disabled:opacity-40"
                                        disabled={saving}
                                        onClick={save}
                                    >
                                        {saving ? "Salvataggio..." : "Salva"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </>
    );
}